/**
 * features/finance/GeneralLedger.tsx
 * ------------------------------------------------------------------
 * The trial balance, and a drill-down into any account.
 *
 * TWO VIEWS, one behind the other, because that is how the question is
 * actually asked: "what does this account say?" followed immediately by
 * "why?". Clicking a line on the trial balance opens its ledger — every
 * movement in date order with a running balance — and the closing figure
 * there is the same number the trial balance showed, computed the other way.
 *
 * NOTHING IS STORED. Every figure on this screen is a reduce over journal
 * lines, computed on render. A stored balance is a balance that drifts from
 * the entries behind it, silently, and then the account and its own history
 * disagree with no way to tell which is right.
 *
 * IF IT DOES NOT BALANCE, THAT IS SHOWN LOUDLY. Every journal is asserted
 * balanced before it is written, so an out-of-balance trial balance means a
 * journal was written around that check or lines have been lost. It is not a
 * rounding curiosity to be quietly rounded away, and the difference is
 * printed rather than hidden.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { Landmark, ChevronLeft, AlertTriangle, ArrowRight } from 'lucide-react';
import { useGlData } from '@/features/finance/useGlData';
import { PageHeader, SoftCard, StatChip, glassFieldClass, ExportBar } from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';
import {
  buildTrialBalance, buildAccountLedger, groupByType, signedBalance, surplusForPeriod,
} from '@/lib/glReport';
import { downloadTablePdf, downloadCsv, type TableExport } from '@/lib/tableExport';
import type { AccountType } from '@/lib/gl';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Blank rather than 0.00 — a column of zeros hides the figures that matter. */
const cell = (n: number) => (n === 0 ? '' : money(n));

export default function GeneralLedger() {
  const [fy, setFy] = useState(`FY${new Date().getFullYear()}`);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [openCode, setOpenCode] = useState<string | null>(null);

  const gl = useGlData(fy);
  const range = useMemo(() => ({ ...(from ? { from } : {}), ...(to ? { to } : {}) }), [from, to]);

  const tb = useMemo(
    () => buildTrialBalance(gl.accounts, gl.journals, range),
    [gl.accounts, gl.journals, range],
  );
  const groups = useMemo(() => groupByType(tb), [tb]);
  const surplus = useMemo(() => surplusForPeriod(tb), [tb]);

  const ledger = useMemo(
    () => (openCode ? buildAccountLedger(openCode, gl.accounts, gl.journals, range) : null),
    [openCode, gl.accounts, gl.journals, range],
  );

  const periodLabel = `${from || 'from the beginning'} — ${to || 'to date'}`;

  /* Money goes to the spreadsheet as a NUMBER via `raw` while the PDF gets
     the formatted string: a bursar who exports a trial balance is going to
     add a column up in Excel, and "10,000.00" as text sums to nothing. */
  const tbExport: TableExport<typeof tb.rows[number]> = {
    title: `Trial Balance · ${fy}`,
    meta: periodLabel,
    columns: [
      { header: 'Code', text: (r) => r.code, width: 18 },
      { header: 'Account', text: (r) => r.name, width: 70 },
      { header: 'Debit', text: (r) => cell(r.dr), raw: (r) => r.dr || null, align: 'right', width: 26 },
      { header: 'Credit', text: (r) => cell(r.cr), raw: (r) => r.cr || null, align: 'right', width: 26 },
    ],
    rows: tb.rows,
    totals: ['', 'TOTAL', money(tb.totalDr), money(tb.totalCr)],
    note: tb.balanced
      ? undefined
      : `OUT OF BALANCE BY ${money(Math.abs(tb.difference))} — do not report off these figures.`,
    generatedBy: 'General Ledger',
  };

  type LedgerRow = {
    date: string; journalNo: string; narration: string;
    dr: number | null; cr: number | null; balance: number;
  };
  const ledgerRows: LedgerRow[] = ledger
    ? [
        { date: '', journalNo: '', narration: 'Opening balance', dr: null, cr: null, balance: ledger.opening },
        ...ledger.entries.map((e) => ({
          date: e.date,
          journalNo: e.journalNo,
          narration: e.lineNarration || e.narration,
          dr: e.dr || null,
          cr: e.cr || null,
          balance: e.balance,
        })),
      ]
    : [];

  const ledgerExport: TableExport<LedgerRow> | null = ledger && {
    title: `${ledger.code} · ${ledger.name}`,
    meta: `${fy} · ${periodLabel}`,
    columns: [
      { header: 'Date', text: (r) => r.date, width: 22 },
      { header: 'Journal', text: (r) => r.journalNo, width: 34 },
      { header: 'Narration', text: (r) => r.narration, width: 70 },
      { header: 'Debit', text: (r) => (r.dr ? money(r.dr) : ''), raw: (r) => r.dr, align: 'right', width: 24 },
      { header: 'Credit', text: (r) => (r.cr ? money(r.cr) : ''), raw: (r) => r.cr, align: 'right', width: 24 },
      { header: 'Balance', text: (r) => money(r.balance), raw: (r) => r.balance, align: 'right', width: 26 },
    ],
    rows: ledgerRows,
    totals: ['', '', 'CLOSING', money(ledger.totalDr), money(ledger.totalCr), money(ledger.closing)],
    orientation: 'l',
    generatedBy: 'General Ledger',
  };

  return (
    <div className="space-y-5">
      <PageHeader icon={Landmark} title="General Ledger" subtitle="Trial balance, and every movement behind it" />

      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Year</label>
          <select value={fy} onChange={(e) => setFy(e.target.value)} className={glassFieldClass('w-auto')}>
            {[0, 1, 2].map((back) => {
              const y = new Date().getFullYear() - back;
              return <option key={y} value={`FY${y}`}>{`FY${y}`}</option>;
            })}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={glassFieldClass('w-auto')} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={glassFieldClass('w-auto')} />
        </div>
        {(from || to) && (
          <button onClick={() => { setFrom(''); setTo(''); }} className="text-xs text-primary hover:underline pb-2">
            Whole year
          </button>
        )}
      </div>

      {gl.loading ? (
        <SoftCard><div className="flex justify-center py-8"><Spinner label="Reading the ledger…" /></div></SoftCard>
      ) : ledger ? (
        /* ---------------- one account ---------------- */
        <>
          <button onClick={() => setOpenCode(null)} className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
            <ChevronLeft size={15} /> Back to the trial balance
          </button>

          <SoftCard>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  {ledger.code} · {ledger.name}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{ledger.type}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <StatChip tone="blue" value={money(signedBalance(ledger.closing, ledger.type))} label="Closing balance" />
                <StatChip tone="gold" value={String(ledger.entries.length)} label="Movements" />
              </div>
            </div>
            {ledgerExport && (
              <ExportBar
                onPdf={() => downloadTablePdf(ledgerExport)}
                onCsv={() => downloadCsv(ledgerExport)}
              />
            )}
          </SoftCard>

          <SoftCard pad="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/50 text-left text-gray-500">
                  <tr>
                    <th className="px-3 py-2.5 font-medium">Date</th>
                    <th className="px-3 py-2.5 font-medium">Journal</th>
                    <th className="px-3 py-2.5 font-medium">Narration</th>
                    <th className="px-3 py-2.5 font-medium text-right">Debit</th>
                    <th className="px-3 py-2.5 font-medium text-right">Credit</th>
                    <th className="px-3 py-2.5 font-medium text-right">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  <tr className="text-gray-500 dark:text-gray-400">
                    <td className="px-3 py-2" colSpan={3}>Opening balance</td>
                    <td /><td />
                    <td className="px-3 py-2 text-right font-medium">{money(ledger.opening)}</td>
                  </tr>
                  {ledger.entries.map((e, i) => (
                    <tr key={`${e.journalId}-${i}`} className={e.reversed ? 'text-gray-400' : ''}>
                      <td className="px-3 py-2 whitespace-nowrap">{e.date}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{e.journalNo}</td>
                      <td className="px-3 py-2">
                        {e.lineNarration || e.narration}
                        {e.reversed && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700">reversed</span>}
                      </td>
                      <td className="px-3 py-2 text-right">{cell(e.dr)}</td>
                      <td className="px-3 py-2 text-right">{cell(e.cr)}</td>
                      <td className="px-3 py-2 text-right font-medium">{money(e.balance)}</td>
                    </tr>
                  ))}
                  {ledger.entries.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">No movement in this period.</td></tr>
                  )}
                </tbody>
                <tfoot className="bg-gray-50 dark:bg-gray-800/50 font-semibold">
                  <tr>
                    <td className="px-3 py-2.5" colSpan={3}>Closing balance</td>
                    <td className="px-3 py-2.5 text-right">{money(ledger.totalDr)}</td>
                    <td className="px-3 py-2.5 text-right">{money(ledger.totalCr)}</td>
                    <td className="px-3 py-2.5 text-right">{money(ledger.closing)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </SoftCard>
        </>
      ) : (
        /* ---------------- trial balance ---------------- */
        <>
          {/* An out-of-balance ledger is not a rounding curiosity. */}
          {!tb.balanced && (
            <div className="rounded-xl border border-red-300 dark:border-red-800/60 bg-red-50 dark:bg-red-900/20 px-4 py-3">
              <p className="text-sm font-semibold text-red-800 dark:text-red-200 flex items-center gap-2">
                <AlertTriangle size={16} /> The ledger does not balance — out by {money(Math.abs(tb.difference))}
              </p>
              <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                Every journal is checked before it is written, so this means a journal was written
                around that check, or lines have been lost. Do not report off these figures until
                it is found.
              </p>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <StatChip tone="blue" value={money(tb.totalDr)} label="Total debits" />
            <StatChip tone="blue" value={money(tb.totalCr)} label="Total credits" />
            <StatChip tone="gold" value={money(surplus.income)} label="Income" />
            <StatChip tone="gold" value={money(surplus.expenditure)} label="Expenditure" />
            <StatChip
              tone={surplus.surplus >= 0 ? 'green' : 'rose'}
              value={money(surplus.surplus)}
              label={surplus.surplus >= 0 ? 'Surplus' : 'Deficit'}
            />
          </div>

          <SoftCard pad="p-0">
            <div className="p-4 pb-0">
              <ExportBar onPdf={() => downloadTablePdf(tbExport)} onCsv={() => downloadCsv(tbExport)} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/50 text-left text-gray-500">
                  <tr>
                    <th className="px-3 py-2.5 font-medium">Code</th>
                    <th className="px-3 py-2.5 font-medium">Account</th>
                    <th className="px-3 py-2.5 font-medium text-right">Debit</th>
                    <th className="px-3 py-2.5 font-medium text-right">Credit</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                {groups.map((g) => (
                  <tbody key={g.type} className="divide-y divide-gray-100 dark:divide-gray-800">
                    <tr className="bg-primary/5">
                      <td colSpan={5} className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
                        {g.label}
                      </td>
                    </tr>
                    {g.rows.map((r) => (
                      <tr
                        key={r.code}
                        onClick={() => setOpenCode(r.code)}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40"
                      >
                        <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                        <td className="px-3 py-2">{r.name}</td>
                        <td className="px-3 py-2 text-right">{cell(r.dr)}</td>
                        <td className="px-3 py-2 text-right">{cell(r.cr)}</td>
                        <td className="px-2 text-gray-300"><ArrowRight size={13} /></td>
                      </tr>
                    ))}
                    <tr className="text-xs text-gray-500 dark:text-gray-400">
                      <td />
                      <td className="px-3 py-1.5 text-right italic">
                        Total {g.label.toLowerCase()}
                      </td>
                      <td colSpan={2} className="px-3 py-1.5 text-right font-medium">
                        {money(g.total)}
                      </td>
                      <td />
                    </tr>
                  </tbody>
                ))}
                <tfoot className="bg-gray-50 dark:bg-gray-800/50 font-semibold">
                  <tr>
                    <td className="px-3 py-2.5" colSpan={2}>TOTAL</td>
                    <td className="px-3 py-2.5 text-right">{money(tb.totalDr)}</td>
                    <td className="px-3 py-2.5 text-right">{money(tb.totalCr)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
              {tb.rows.length === 0 && (
                <p className="px-3 py-8 text-center text-sm text-gray-500">
                  Nothing has been posted in {fy} yet.
                </p>
              )}
            </div>
          </SoftCard>
        </>
      )}
    </div>
  );
}

/** Kept for the type import above to stay meaningful to a reader. */
export type { AccountType };
